<template>
  <v-dialog v-model="showModal" width="800" @click:outside="closeDialog">
    <v-card max-width="800" v-if="disposal">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h5 text-medium-emphasis ps-2 title-text">allocate parcels</div>
        <v-btn icon="mdi-close" variant="text" @click="closeDialog"></v-btn>
      </v-card-title>
      <v-divider class="mb-4"></v-divider>
      <v-card-text>
        <!-- sell transaction summary -->
        <v-sheet class="mb-4 pa-3 bg-grey-lighten-4 rounded">
          <div class="text-subtitle-2">sell transaction</div>
          <div>{{ disposal.ticker }} | {{ showDateFormat(disposal.sell_txn.txn_date) }} | {{ disposal.sell_txn.qty }} units | price (AUD): {{ formatCurrency(getSellNet() / disposal.sell_txn.qty) }} | proceeds: {{ formatCurrency(getProceeds()) }}</div>
        </v-sheet>

        <!-- pre-fill method -->
        <v-radio-group v-model="selectedMethod" inline density="compact" class="mb-3" hide-details>
          <v-radio label="FIFO" value="fifo"></v-radio>
          <v-radio label="LIFO" value="lifo"></v-radio>
          <v-radio label="LOFO" value="lofo"></v-radio>
          <v-radio label="HIFO" value="hifo"></v-radio>

          <v-radio label="Custom" value="custom"></v-radio>
        </v-radio-group>

        <!-- available parcels -->
        <table class="parcel-table">
          <tr>
            <th>buy date</th>
            <th>total qty</th>
            <th>available</th>
            <th>allocate qty</th>
            <th>unit cost</th>
            <th>days held</th>
            <th>discount</th>
          </tr>
          <tr v-for="(parcel, index) in parcels" :key="parcel.buy_txn.id">
            <td>{{ showDateFormat(parcel.buy_txn.txn_date) }}</td>
            <td>{{ parcel.total_qty }}</td>
            <td>{{ parcel.remaining_qty.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 3}) }}</td>
            <td>
              <v-text-field
                v-model.number="parcel.allocate_qty"
                type="number"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 120px"
                :max="parcel.remaining_qty"
                min="0"
                step="any"
                @update:modelValue="selectedMethod = 'custom'"
              ></v-text-field>
            </td>
            <td>{{ formatCurrency(getUnitCost(parcel.buy_txn)) }}</td>
            <td>{{ getHoldingDays(parcel.buy_txn) }}</td>
            <td>{{ getHoldingDays(parcel.buy_txn) > 365 ? 'yes' : 'no' }}</td>
          </tr>
        </table>

        <!-- live summary -->
        <v-divider class="my-4"></v-divider>
        <v-row>
          <v-col cols="3">
            <div class="text-caption text-grey">allocated</div>
            <div :class="allocationValid ? '' : 'text-red'">{{ totalAllocated.toLocaleString("en", {minimumFractionDigits: 0, maximumFractionDigits: 3}) }} / {{ disposal.sell_txn.qty }}</div>
          </v-col>
          <v-col cols="3">
            <div class="text-caption text-grey">cost base</div>
            <div>{{ formatCurrency(displayCostBase) }}</div>
          </v-col>
          <v-col cols="3">
            <div class="text-caption text-grey">capital gain</div>
            <div :class="displayGain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(displayGain) }}</div>
          </v-col>
          <v-col cols="3">
            <div class="text-caption text-grey">gain type</div>
            <div><v-chip size="x-small" :color="liveGainType.color" variant="tonal">{{ liveGainType.label }}</v-chip></div>
          </v-col>
        </v-row>
      </v-card-text>
      <template v-slot:actions>
        <v-btn @click="saveAllocation" :disabled="!allocationValid" :loading="saving" class="mt-2 submit_button" block>save allocation</v-btn>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';
import moment from 'moment';

export default {
  props: {
    show: Boolean,
    disposal: Object
  },
  data() {
    return {
      parcels: [],
      selectedMethod: 'fifo',
      saving: false
    };
  },
  computed: {
    ...mapState({ token: "token" }),
    showModal: {
      get() { return this.show; }
    },
    totalAllocated() {
      return this.parcels.reduce((sum, p) => sum + (p.allocate_qty || 0), 0);
    },
    allocationValid() {
      if (!this.disposal) return false;
      return Math.abs(this.totalAllocated - this.disposal.sell_txn.qty) < 0.005;
    },
    liveCostBase() {
      return this.parcels.reduce((sum, p) => {
        if (!p.allocate_qty) return sum;
        return sum + p.allocate_qty * this.getUnitCost(p.buy_txn);
      }, 0);
    },
    liveProceeds() {
      if (!this.disposal) return 0;
      const sell = this.disposal.sell_txn;
      const net = this.getSellNet();
      return this.totalAllocated * (net / sell.qty);
    },
    liveGain() {
      return this.liveProceeds - this.liveCostBase;
    },
    activePreview() {
      if (!this.disposal) return null;
      const map = {
        fifo: this.disposal.fifo_preview,
        lifo: this.disposal.lifo_preview,
        lofo: this.disposal.lofo_preview,
        hifo: this.disposal.hifo_preview,
      };
      return map[this.selectedMethod] || null;
    },
    displayCostBase() {
      const preview = this.activePreview;
      if (preview) return preview.allocations.reduce((sum, a) => sum + a.cost_base, 0);
      return this.liveCostBase;
    },
    displayGain() {
      const preview = this.activePreview;
      if (preview) return preview.total_gain;
      return this.liveGain;
    },
    liveGainType() {
      const gain = this.displayGain;
      if (gain < 0) return { label: 'loss', color: 'red' };
      const preview = this.activePreview;
      if (preview) {
        let discountGain = 0;
        let nonDiscountGain = 0;
        for (const a of preview.allocations) {
          if (a.gain_type === 'discount_gain') discountGain += a.capital_gain;
          else if (a.gain_type === 'non_discount_gain') nonDiscountGain += a.capital_gain;
        }
        if (discountGain > nonDiscountGain) return { label: 'discount', color: 'green' };
        return { label: 'non-discount', color: 'orange' };
      }
      // fallback for custom
      let discountQty = 0;
      let nonDiscountQty = 0;
      for (const p of this.parcels) {
        if (!p.allocate_qty) continue;
        if (this.getHoldingDays(p.buy_txn) > 365) {
          discountQty += p.allocate_qty;
        } else {
          nonDiscountQty += p.allocate_qty;
        }
      }
      if (discountQty > nonDiscountQty) return { label: 'discount', color: 'green' };
      return { label: 'non-discount', color: 'orange' };
    }
  },
  watch: {
    disposal: {
      handler(val) {
        if (val) {
          this.initParcels();
        }
      },
      immediate: true
    },
    selectedMethod(val) {
      if (val === 'fifo') this.applyFIFO();
      else if (val === 'lifo') this.applyLIFO();
      else if (val === 'lofo') this.applyLOFO();
      else if (val === 'hifo') this.applyHIFO();
    }
  },
  methods: {
    ...mapMutations(["showSnackbar"]),
    initParcels() {
      if (!this.disposal) return;
      this.parcels = this.disposal.available_parcels.map(p => ({
        ...p,
        allocate_qty: 0
      }));
      this.selectedMethod = 'fifo';
      this.applyFIFO();
    },
    applyFIFO() {
      let remaining = this.disposal.sell_txn.qty;
      for (const parcel of this.parcels) {
        const qty = Math.min(remaining, parcel.remaining_qty);
        parcel.allocate_qty = qty > 0.0005 ? parseFloat(qty.toFixed(3)) : 0;
        remaining -= qty;
      }
    },
    applyLIFO() {
      // zero out first
      for (const parcel of this.parcels) {
        parcel.allocate_qty = 0;
      }
      let remaining = this.disposal.sell_txn.qty;
      for (let i = this.parcels.length - 1; i >= 0; i--) {
        const parcel = this.parcels[i];
        const qty = Math.min(remaining, parcel.remaining_qty);
        parcel.allocate_qty = qty > 0.0005 ? parseFloat(qty.toFixed(3)) : 0;
        remaining -= qty;
      }
    },
    applyLOFO() {
      // sort by unit cost ascending (lowest first), allocate in that order
      for (const parcel of this.parcels) {
        parcel.allocate_qty = 0;
      }
      const sorted = [...this.parcels].sort((a, b) => this.getUnitCost(a.buy_txn) - this.getUnitCost(b.buy_txn));
      let remaining = this.disposal.sell_txn.qty;
      for (const parcel of sorted) {
        const qty = Math.min(remaining, parcel.remaining_qty);
        parcel.allocate_qty = qty > 0.0005 ? parseFloat(qty.toFixed(3)) : 0;
        remaining -= qty;
      }
    },
    applyHIFO() {
      // sort by unit cost descending (highest first), allocate in that order
      for (const parcel of this.parcels) {
        parcel.allocate_qty = 0;
      }
      const sorted = [...this.parcels].sort((a, b) => this.getUnitCost(b.buy_txn) - this.getUnitCost(a.buy_txn));
      let remaining = this.disposal.sell_txn.qty;
      for (const parcel of sorted) {
        const qty = Math.min(remaining, parcel.remaining_qty);
        parcel.allocate_qty = qty > 0.0005 ? parseFloat(qty.toFixed(3)) : 0;
        remaining -= qty;
      }
    },
    getUnitCost(buy_txn) {
      const net = (buy_txn.currency && buy_txn.currency !== 'AUD' && buy_txn.base_currency_net_amount)
        ? buy_txn.base_currency_net_amount
        : buy_txn.net_amount;
      return net / buy_txn.qty;
    },
    getSellNet() {
      const sell = this.disposal.sell_txn;
      return (sell.currency && sell.currency !== 'AUD' && sell.base_currency_net_amount)
        ? sell.base_currency_net_amount
        : sell.net_amount;
    },
    getProceeds() {
      const sell = this.disposal.sell_txn;
      return this.getSellNet();
    },
    getHoldingDays(buy_txn) {
      const buy = new Date(buy_txn.txn_date);
      const sell = new Date(this.disposal.sell_txn.txn_date);
      return Math.floor((sell - buy) / (1000 * 60 * 60 * 24));
    },
    showDateFormat(rawDate) {
      return moment(rawDate).format('DD/MM/YYYY');
    },
    formatCurrency(val) {
      if (val == null) return '-';
      return val.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async saveAllocation() {
      this.saving = true;
      try {
        const allocations = this.parcels
          .filter(p => p.allocate_qty > 0)
          .map(p => ({
            buy_txn_id: p.buy_txn.id,
            qty: p.allocate_qty
          }));

        await axios.post(
          "/reports/cgtdisposals/allocate",
          {
            sell_txn_id: this.disposal.sell_txn.id,
            allocations,
            method: this.selectedMethod
          },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.$emit('saved');
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to save allocation';
        this.showSnackbar({ message });
      }
      this.saving = false;
    },
    closeDialog() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.v-card {
  font-family: "Space Grotesk", "Courier New", Courier, monospace, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.v-card :deep(*) {
  font-family: "Space Grotesk", "Courier New", Courier, monospace, sans-serif !important;
}

.parcel-table {
  width: 100%;
  border-collapse: collapse;
}

.parcel-table th, .parcel-table td {
  padding: 6px 8px;
  text-align: left;
}
</style>
